import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, ProgressBar, Modal, Alert, Collapse, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { showToast } from '../../hooks/useToast';

const styles = `
  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Courses = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);
  const [stats, setStats] = useState({ total: 0, synced: 0, percentage: 0 });
  const [allCourses, setAllCourses] = useState([]);
  const [pendingCourses, setPendingCourses] = useState([]);
  const [courseStudents, setCourseStudents] = useState({});
  const [enrollingCourse, setEnrollingCourse] = useState(null);
  const [enrollingUser, setEnrollingUser] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({ show: false, current: 0, total: 0 });
  const [categoriesReady, setCategoriesReady] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    await Promise.all([
      checkCategoriesStatus(),
      loadAllCourses()
    ]);
    setIsLoading(false);
  };

  const loadAllCourses = async () => {
    try {
      const response = await api.get('/courses/all');
      const courses = response.data.courses || [];
      setAllCourses(courses);
      setPendingCourses(courses.filter(c => !c.lms_id));

      // Update stats locally
      const total = courses.length;
      const synced = courses.filter(c => c.lms_id).length;
      setStats({
        total,
        synced,
        percentage: total > 0 ? Math.round((synced / total) * 100) : 0
      });
    } catch (error) {
      console.error('Error loading all courses:', error);
      showToast('Error cargando cursos', 'danger');
    }
  };

  const checkCategoriesStatus = async () => {
    try {
      const response = await api.get('/categories/stats');
      const categoryData = response.data;

      const allCategoriesMigrated = Object.values(categoryData).every(
        category => category.total === category.synced
      );

      setCategoriesReady(allCategoriesMigrated);
    } catch (error) {
      setCategoriesReady(false);
    }
  };


  const loadCourseEnrollments = async (courseId) => {
    try {
      const response = await api.get(`/courses/${courseId}/students`);
      setCourseStudents(prev => ({
        ...prev,
        [courseId]: response.data
      }));
      setExpandedCourse(courseId);
    } catch (error) {
      showToast('Error cargando estudiantes del curso', 'danger');
    }
  };

  const enrollCourseUsers = async (courseId) => {
    setEnrollingCourse(courseId);
    try {
      const response = await api.post(`/courses/${courseId}/enroll`);

      const { enrolled, created, errors } = response.data;
      let message = [];

      if (enrolled.length > 0) message.push(`${enrolled.length} usuarios matriculados`);
      if (created.length > 0) message.push(`${created.length} usuarios creados`);
      if (errors.length > 0) message.push(`${errors.length} errores`);

      showToast(message.join(', '), errors.length > 0 ? 'warning' : 'success');

      // Recargar datos del curso
      await loadCourseEnrollments(courseId);
      // Recargar estadísticas globales para actualizar contadores
      await loadAllCourses();
    } catch (error) {
      showToast('Error matriculando usuarios', 'danger');
    } finally {
      setEnrollingCourse(null);
    }
  };

  const enrollSingleUser = async (courseId, userId) => {
    setEnrollingUser(userId);
    try {
      const response = await api.post(`/courses/${courseId}/enroll/${userId}`);
      const message = response.data.user.created
        ? `${response.data.user.name} creado y matriculado`
        : `${response.data.user.name} matriculado`;
      showToast(message, 'success');
      await loadCourseEnrollments(courseId);
      await loadAllCourses();
    } catch (error) {
      showToast('Error matriculando usuario', 'danger');
    } finally {
      setEnrollingUser(null);
    }
  };

  const migrateCourses = async () => {
    if (isProcessing || !categoriesReady) return;
    setIsProcessing(true);

    const totalItems = stats.total - stats.synced;
    setMigrationProgress({ show: true, current: 0, total: totalItems });

    try {
      let hasMore = true;
      let totalProcessed = 0;

      while (hasMore) {
        const response = await api.post('/courses/migrate', { batchSize: 5 });

        totalProcessed += response.data.processed;
        hasMore = response.data.hasMore;

        setMigrationProgress(prev => ({ ...prev, current: totalProcessed }));

        if (response.data.processed === 0) break;
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      showToast(`Cursos migrados: ${totalProcessed}`, 'success');
      await loadInitialData();
    } catch (error) {
      showToast('Error migrando cursos', 'danger');
    } finally {
      setIsProcessing(false);
      setMigrationProgress({ show: false, current: 0, total: 0 });
    }
  };

  const enrollMissingInAllCourses = async () => {
    if (isProcessing) return;

    // Identificar cursos que tienen alumnos pendientes (migrated < total)
    const coursesWithPending = allCourses.filter(course =>
      course.lms_id && parseInt(course.migrated_students || 0) < parseInt(course.total_students || 0)
    );

    if (coursesWithPending.length === 0) {
      showToast('No se encontraron cursos con estudiantes pendientes', 'info');
      return;
    }

    setIsProcessing(true);
    setMigrationProgress({
      show: true,
      current: 0,
      total: coursesWithPending.length,
      title: 'Matriculando Faltantes',
      status: `Iniciando proceso para ${coursesWithPending.length} cursos...`,
      details: 'Preparando...'
    });

    try {
      for (let i = 0; i < coursesWithPending.length; i++) {
        const course = coursesWithPending[i];
        setMigrationProgress(prev => ({
          ...prev,
          current: i,
          status: `Procesando curso ${i + 1} de ${coursesWithPending.length}`,
          details: `Matriculando en: ${course.subject_name}`
        }));

        // El endpoint /enroll procesa todos los alumnos faltantes del curso
        const response = await api.post(`/courses/${course.id}/enroll`);
        const { enrolled, created, errors: courseErrors } = response.data;

        setMigrationProgress(prev => ({
          ...prev,
          details: `Finalizado: ${course.subject_name} (+${enrolled?.length || 0} alumnos)`
        }));

        if (courseErrors?.length > 0) {
          console.warn(`Errores en curso ${course.id}:`, courseErrors);
        }

        // Pequeña pausa para no saturar
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      showToast('Proceso de matriculación secuencial completado', 'success');
      await loadInitialData();
    } catch (error) {
      console.error('Error en matriculación secuencial:', error);
      showToast('Error en la matriculación secuencial', 'danger');
    } finally {
      setIsProcessing(false);
      setMigrationProgress(prev => ({ ...prev, show: false }));
    }
  };

  const syncAllEnrollments = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    // Initialize progress modal
    setMigrationProgress({
      show: true,
      current: 0,
      total: 0,
      title: 'Sincronizando Matriculaciones',
      status: 'Iniciando...',
      details: 'Preparando conexión...'
    });

    try {
      // Use fetch for SSE (axios doesn't handle streams well natively in browser)
      // Get the token from the api instance if possible, or just assume session cookie/auth header 
      // is handled. If using a bearer token in 'api' instance, we need to replicate it here.
      // Assuming api.defaults.headers.common['Authorization'] might be set.

      const response = await fetch(`${api.defaults.baseURL}/courses/enroll-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...api.defaults.headers.common
        },
        body: JSON.stringify({ concurrency: 5, batchSize: 50 })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));

              if (data.type === 'start') {
                setMigrationProgress(prev => ({
                  ...prev,
                  total: data.totalCourses,
                  status: 'Procesando cursos...'
                }));
              } else if (data.type === 'progress') {
                setMigrationProgress(prev => ({
                  ...prev,
                  current: data.processedCourses,
                  details: `Curso actual: ${data.currentCourse || '...'}`
                }));
              } else if (data.type === 'complete') {
                showToast(`Sincronización finalizada. Exitosos: ${data.successEnrollments}, Fallidos: ${data.failedEnrollments}`, 'success');
                if (data.errors && data.errors.length > 0) {
                  console.warn('Errores de sincronización:', data.errors);
                }
              } else if (data.type === 'error') {
                showToast(`Error: ${data.message}`, 'danger');
              }
            } catch (e) {
              // Ignore parsing errors for partial chunks
            }
          }
        }
      }

      await loadInitialData();
    } catch (error) {
      showToast('Error en sincronización masiva', 'danger');
      console.error(error);
    } finally {
      setIsProcessing(false);
      setMigrationProgress(prev => ({ ...prev, show: false }));
    }
  };

  const getProgressPercentage = (synced, total) => total > 0 ? Math.round((synced / total) * 100) : 0;
  const percentage = getProgressPercentage(stats.synced, stats.total);
  const pending = stats.total - stats.synced;

  const CourseRow = ({ course, isPending = false }) => {
    const isExpanded = expandedCourse === course.id;
    const students = courseStudents[course.id];
    const hasStudents = students && students.local.length > 0;

    return (
      <>
        <tr>
          <td>
            <div className="d-flex align-items-center">
              <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '35px', height: '35px' }}>
                <i className="bi bi-book"></i>
              </div>
              <div>
                <div className="fw-medium">{course.subject_name}</div>
                <small className="text-muted">ID: {course.id}</small>
              </div>
            </div>
          </td>
          <td>
            <span className="text-muted">{course.section_name || 'Sin sección'}</span>
          </td>
          <td>
            {course.teacher_name ? (
              <div className="text-muted small">
                <i className="bi bi-person-badge me-1"></i>{course.teacher_name}
              </div>
            ) : <span className="text-muted small">Sin profesor</span>}
          </td>
          <td>
            {course.coordinator_name ? (
              <div className="text-primary small">
                <i className="bi bi-person-gear me-1"></i>{course.coordinator_name}
              </div>
            ) : <span className="text-muted small">Sin coordinador</span>}
          </td>
          <td style={{ minWidth: '140px' }}>
            {parseInt(course.total_students || 0) > 0 ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-${course.id}`}>
                    {parseInt(course.total_students) - parseInt(course.migrated_students || 0) > 0
                      ? `Atención: Faltan ${parseInt(course.total_students) - parseInt(course.migrated_students || 0)} estudiantes`
                      : "Todos migrados"}
                  </Tooltip>
                }
              >
                <div className="d-flex align-items-center">
                  <div style={{ width: '100%' }}>
                    <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.75rem' }}>
                      <span className={parseInt(course.migrated_students || 0) < parseInt(course.total_students) ? "text-danger fw-bold" : "text-success fw-bold"}>
                        {course.migrated_students || 0}
                      </span>
                      <span className="text-muted">/ {course.total_students}</span>
                    </div>
                    <ProgressBar
                      now={((course.migrated_students || 0) / course.total_students) * 100}
                      variant={parseInt(course.migrated_students || 0) === parseInt(course.total_students) ? "success" : "warning"}
                      style={{ height: '6px' }}
                    />
                  </div>
                </div>
              </OverlayTrigger>
            ) : (
              <small className="text-muted">Sin estudiantes</small>
            )}
          </td>
          <td>
            {isPending ? (
              <Badge bg="warning">Pendiente</Badge>
            ) : (
              <Badge bg="success">Migrado</Badge>
            )}
          </td>
          <td>
            <div className="d-flex gap-1">
              {!isPending && course.lms_id && (
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => isExpanded ? setExpandedCourse(null) : loadCourseEnrollments(course.id)}
                  >
                    <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => enrollCourseUsers(course.id)}
                    disabled={enrollingCourse === course.id}
                    title="Matricular todos"
                  >
                    {enrollingCourse === course.id ? (
                      <i className="bi bi-arrow-repeat spin"></i>
                    ) : (
                      <i className="bi bi-people-fill"></i>
                    )}
                  </Button>
                </>
              )}
            </div>
          </td>
        </tr>
        {isExpanded && (
          <tr>
            <td colSpan="6" className="p-0">
              <Collapse in={isExpanded}>
                <div className="border-top">
                  {students ? (
                    hasStudents ? (
                      <div className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h6 className="mb-1">Estado de Matriculación</h6>
                            <small className="text-muted">{students.local.length} participantes total</small>
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => enrollCourseUsers(course.id)}
                            disabled={enrollingCourse === course.id}
                          >
                            {enrollingCourse === course.id ? (
                              <><i className="bi bi-arrow-repeat spin me-1"></i>Matriculando...</>
                            ) : (
                              <><i className="bi bi-people-fill me-1"></i>Matricular Todo el Curso</>
                            )}
                          </Button>
                        </div>

                        <Row className="g-2">
                          <Col md={6}>
                            <div className="border border-success rounded p-2">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <small className="text-success fw-bold">
                                  <i className="bi bi-check-circle me-1"></i>Matriculados
                                </small>
                                <Badge bg="success">{students.enrolled.length}</Badge>
                              </div>
                              {students.enrolled.length > 0 ? (
                                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                  {students.enrolled.map((user, index) => (
                                    <div key={index} className="d-flex align-items-center py-1">
                                      <i className={`bi ${user.role === 'Coordinador' ? 'bi-person-gear' : user.role === 'Profesor' ? 'bi-person-workspace' : 'bi-check'} ${user.role === 'Coordinador' ? 'text-primary' : 'text-success'} me-2`} style={{ fontSize: '12px' }}></i>
                                      <div>
                                        <span className="small">{user.first_name} {user.last_name}</span>
                                        <Badge bg={user.role === 'Coordinador' ? 'primary' : user.role === 'Profesor' ? 'info' : 'secondary'} className="ms-1" style={{ fontSize: '9px' }}>{user.role}</Badge>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <small className="text-muted">Ninguno matriculado</small>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="border border-warning rounded p-2">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <small className="text-warning fw-bold">
                                  <i className="bi bi-clock me-1"></i>Pendientes
                                </small>
                                <Badge bg="warning">{students.notEnrolled.length}</Badge>
                              </div>
                              {students.notEnrolled.length > 0 ? (
                                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                  {students.notEnrolled.map((user, index) => (
                                    <div key={index} className="d-flex align-items-center justify-content-between py-1">
                                      <div className="d-flex align-items-center flex-grow-1">
                                        <i className={`bi ${user.role === 'Coordinador' ? 'bi-person-gear' : user.role === 'Profesor' ? 'bi-person-workspace' : 'bi-clock'} ${user.role === 'Coordinador' ? 'text-primary' : 'text-warning'} me-2`} style={{ fontSize: '12px' }}></i>
                                        <div>
                                          <span className="small">{user.first_name} {user.last_name}</span>
                                          <Badge bg={user.role === 'Coordinador' ? 'primary' : user.role === 'Profesor' ? 'info' : 'secondary'} className="ms-1" style={{ fontSize: '9px' }}>{user.role}</Badge>
                                          {!user.lms_id && <Badge bg="danger" className="ms-1" style={{ fontSize: '9px' }}>No migrado</Badge>}
                                        </div>
                                      </div>
                                      <Button
                                        variant={user.lms_id ? "success" : "warning"}
                                        size="sm"
                                        onClick={() => enrollSingleUser(course.id, user.id)}
                                        disabled={enrollingUser === user.id}
                                        title={user.lms_id ? `Matricular ${user.role.toLowerCase()}` : "Crear usuario y matricular"}
                                      >
                                        {enrollingUser === user.id ? (
                                          <i className="bi bi-arrow-repeat spin"></i>
                                        ) : user.lms_id ? (
                                          <i className="bi bi-plus-circle"></i>
                                        ) : (
                                          <i className="bi bi-person-plus"></i>
                                        )}
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <small className="text-success">Todos matriculados</small>
                              )}
                            </div>
                          </Col>
                        </Row>

                        <div className="mt-2 p-2 bg-light rounded">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="text-muted">Progreso de Estudiantes</small>
                            <small className="fw-bold">
                              {students.totalStudents > 0 ? Math.round((students.migratedStudents / students.totalStudents) * 100) : 0}%
                            </small>
                          </div>
                          <ProgressBar
                            now={students.totalStudents > 0 ? (students.migratedStudents / students.totalStudents) * 100 : 0}
                            variant="success"
                            style={{ height: '6px' }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-3">
                        <i className="bi bi-person-x fs-3 text-muted"></i>
                        <p className="text-muted small mb-0">No hay estudiantes asignados</p>
                      </div>
                    )
                  ) : (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                      <p className="text-muted small mt-2 mb-0">Cargando...</p>
                    </div>
                  )}
                </div>
              </Collapse>
            </td>
          </tr>
        )}
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="text-muted">Cargando información de cursos...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Gestión de Cursos</h2>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate('/courses/compare')}
            className="me-2"
          >
            <i className="bi bi-arrow-left-right me-1"></i>
            Comparar con Moodle
          </Button>
          <Badge bg="success">{stats.synced} Migrados</Badge>
          <Badge bg="warning">{pending} Pendientes</Badge>
        </div>
      </div>

      {!categoriesReady && (
        <Alert variant="warning" className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Categorías requeridas:</strong> Debe migrar todas las categorías antes de migrar cursos.
        </Alert>
      )}

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">Progreso de Migración</h5>
              <small className="text-muted">Cursos habilitados para Moodle</small>
            </div>
            <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-book fs-4"></i>
            </div>
          </div>

          <ProgressBar now={percentage} variant="success" className="mb-3" style={{ height: '12px' }} />

          <div className="row text-center mb-4">
            <div className="col-4">
              <div className="h4 text-success mb-0">{stats.synced}</div>
              <small className="text-muted">Migrados</small>
            </div>
            <div className="col-4">
              <div className="h4 text-warning mb-0">{pending}</div>
              <small className="text-muted">Pendientes</small>
            </div>
            <div className="col-4">
              <div className="h4 text-secondary mb-0">{stats.total}</div>
              <small className="text-muted">Total</small>
            </div>
          </div>

          <Button
            variant="outline-primary"
            className="w-100 mb-2"
            onClick={syncAllEnrollments}
            disabled={isProcessing || !categoriesReady}
          >
            <i className="bi bi-people-fill me-1"></i>
            Sincronización Total (SSE)
          </Button>

          <Button
            variant="warning"
            className="w-100 mb-2 text-dark"
            onClick={enrollMissingInAllCourses}
            disabled={isProcessing || !categoriesReady}
          >
            <i className="bi bi-person-check-fill me-1"></i>
            Matricular Faltantes (1 a 1)
          </Button>

          <Button
            variant={pending > 0 && categoriesReady ? 'success' : 'outline-secondary'}
            className="w-100"
            onClick={migrateCourses}
            disabled={isProcessing || pending === 0 || !categoriesReady}
          >
            <i className="bi bi-cloud-upload me-1"></i>
            {pending === 0 ? 'Todos los cursos migrados' :
              !categoriesReady ? 'Esperando categorías' :
                isProcessing ? 'Migrando cursos...' : `Migrar ${pending} cursos`}
          </Button>
        </Card.Body>
      </Card>

      {/* Cursos Migrados */}
      {stats.synced > 0 && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-success text-white">
            <h6 className="mb-0">
              <i className="bi bi-check-circle me-2"></i>
              Cursos Migrados ({stats.synced})
            </h6>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Materia</th>
                  <th>Sección</th>
                  <th>Profesor</th>
                  <th>Coordinador</th>
                  <th>Estudiantes</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {allCourses
                  .filter(course => course.lms_id)
                  .sort((a, b) => {
                    const pendingA = (parseInt(a.total_students || 0) - parseInt(a.migrated_students || 0));
                    const pendingB = (parseInt(b.total_students || 0) - parseInt(b.migrated_students || 0));

                    if (pendingA !== pendingB) return pendingB - pendingA;

                    const levelA = parseInt(a.level_order || 0);
                    const levelB = parseInt(b.level_order || 0);
                    if (levelA !== levelB) return levelA - levelB;

                    const secA = a.raw_section_name || '';
                    const secB = b.raw_section_name || '';
                    if (secA !== secB) return secA.localeCompare(secB);

                    return (a.raw_subject_name || '').localeCompare(b.raw_subject_name || '');
                  })
                  .map((course, index) => (
                    <CourseRow key={`migrated-${index}`} course={course} isPending={false} />
                  ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Cursos Pendientes */}
      {pending > 0 && categoriesReady && pendingCourses.length > 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-warning text-dark">
            <h6 className="mb-0">
              <i className="bi bi-clock me-2"></i>
              Cursos Pendientes de Migración ({pendingCourses.length})
            </h6>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Materia</th>
                  <th>Sección</th>
                  <th>Profesor</th>
                  <th>Coordinador</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pendingCourses.map((course, index) => (
                  <CourseRow key={`pending-${index}`} course={course} isPending={true} />
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {pending === 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 text-success">¡Todos los cursos están migrados!</h5>
            <p className="text-muted">Los {stats.total} cursos del sistema han sido migrados exitosamente a Moodle</p>
          </Card.Body>
        </Card>
      )}

      <Modal show={migrationProgress.show} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center py-4">
          <div className="mb-3">
            <div className="spinner-border text-success mb-3" role="status">
              <span className="visually-hidden">Procesando...</span>
            </div>
            <h5>{migrationProgress.title || 'Migrando Cursos'}</h5>
            <p className="text-muted mb-0">
              {migrationProgress.status || `${migrationProgress.current} de ${migrationProgress.total} procesados`}
            </p>
            {migrationProgress.details && (
              <small className="text-secondary d-block mt-2">
                {migrationProgress.details}
              </small>
            )}
          </div>

          <ProgressBar
            now={migrationProgress.total > 0 ? (migrationProgress.current / migrationProgress.total) * 100 : 0}
            variant="success"
            className="mb-3"
            style={{ height: '10px' }}
          />

          <div className="text-muted">
            {migrationProgress.total > 0 ? Math.round((migrationProgress.current / migrationProgress.total) * 100) : 0}% completado
            {migrationProgress.total > 0 && ` (${migrationProgress.current}/${migrationProgress.total})`}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Courses;